import { useInfiniteQuery } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import { SYNC_LOGS } from '@/data/syncLogs';
import type { SyncLogRow } from '@/data/syncLogs';

interface SyncLogsPage {
  syncLogs: SyncLogRow[];
  nextCursor: string | null;
}

interface UseSyncLogsOptions {
  collectorId: string;
  /**
   * `enabled` gates the query so it only fires when the user has expanded
   * the sync-log section for this collector. Saves pointless requests
   * when every card is collapsed (the default).
   */
  enabled: boolean;
  /**
   * Items per page — mirrors the backend `limit` query param. Default
   * 10; clamped server-side to 50.
   */
  limit?: number;
}

interface UseSyncLogsResult {
  pages: SyncLogsPage[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  error: Error | null;
}

/**
 * Cursor-paginated per-collector SyncLog history. Keyed by
 * (orgId, collectorId) so each card's pagination state is independent.
 * In mockup mode returns canned pages from SYNC_LOGS sliced by limit.
 */
export function useSyncLogs({
  collectorId,
  enabled,
  limit = 10,
}: UseSyncLogsOptions): UseSyncLogsResult {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;

  const query = useInfiniteQuery<SyncLogsPage, Error>({
    queryKey: ['syncLogs', orgId, collectorId, limit],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      if (mockupMode) {
        return mockupPage(collectorId, pageParam as string | undefined, limit);
      }
      const token = await getToken();
      const qs = new URLSearchParams({ limit: String(limit) });
      if (typeof pageParam === 'string') qs.set('before', pageParam);
      return apiFetch<SyncLogsPage>(
        `/api/v1/orgs/${orgId}/collectors/${collectorId}/sync-logs?${qs.toString()}`,
        token,
      );
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: enabled && (mockupMode || !!orgId),
  });

  return {
    pages: query.data?.pages ?? [],
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: () => {
      void query.fetchNextPage();
    },
    error: query.error instanceof Error ? query.error : null,
  };
}

function mockupPage(collectorId: string, before: string | undefined, limit: number): SyncLogsPage {
  const all = SYNC_LOGS[collectorId] ?? [];
  const filtered = before
    ? all.filter((row) => Date.parse(row.startedAt) < Date.parse(before))
    : all;
  const page = filtered.slice(0, limit);
  const nextCursor = page.length === limit ? (page[page.length - 1]?.startedAt ?? null) : null;
  return { syncLogs: page, nextCursor };
}
